import { Button } from 'primereact/button';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalCourseStore from '../../../../../../../stores/global-course-store';
import { completeLectureOperation } from '../operations/complete-lecture-operation';
import globalLectureBuilderStore from '../../../../../../../stores/global-lecture-builder-store';
import { UserSummaryType } from '../../../../../../../api/types/user/user-summary.type';
import globalProgramStore from '../../../../../../../stores/program-store';
import {
  AttendanceDataForm,
  AttendanceSessionPayload,
} from './attendance-data-form';
import { LectureTimePicker } from './lecture-time-picker';
import { LectureAttendanceSessionWithAttendanceDB } from '../../../../../../../api/types/lecturers/details/lecture-details';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { toast } from 'react-toastify';

interface LecturePageAttendanceDatamodel {
  attendanceSessions?: LectureAttendanceSessionWithAttendanceDB[];
}
export const AttendanceLessonNotStartedDataForm: React.FC<LecturePageAttendanceDatamodel> =
  observer(({ attendanceSessions }) => {
    const [shouldAllowComplete, setShouldAllowComplete] = useState(false);

    const lecture = useMemo(
      () => globalLectureBuilderStore.selectedLecture,
      [globalLectureBuilderStore.selectedLecture],
    );

    const [startTime, setStartTime] = useState<Date>(
      lecture?.schedule?.startTime
        ? new Date(lecture.schedule.startTime)
        : new Date(),
    );
    const [endTime, setEndTime] = useState<Date>(
      lecture?.schedule?.endTime
        ? new Date(lecture.schedule.endTime)
        : new Date(),
    );
    const [addBreak, setAddBreak] = useState(false);
    const [breakMinutes, setBreakMinutes] = useState<number | null>(null);

    useEffect(() => {
      const currentBreakMinutes = lecture?.item?.lectureBreakMinutes;
      setAddBreak(
        currentBreakMinutes !== null && currentBreakMinutes !== undefined,
      );
      setBreakMinutes(
        currentBreakMinutes !== null && currentBreakMinutes !== undefined
          ? currentBreakMinutes
          : null,
      );
    }, [lecture?.item?.id, lecture?.item?.lectureBreakMinutes]);

    const currentStudents: UserSummaryType[] =
      globalCourseStore.getCurrentCourse()?.details?.students ?? [];

    const sessions = useMemo<LectureAttendanceSessionWithAttendanceDB[]>(() => {
      if (attendanceSessions && attendanceSessions.length > 0) {
        return attendanceSessions;
      }

      const sessionCount = lecture?.item?.attendanceSessionNumber ?? 0;
      if (sessionCount <= 0) {
        return [];
      }

      const baseEntity = {
        createdAt: new Date(),
        createdBy: 0,
        createdByName: '',
      };

      return Array.from({ length: sessionCount }, (_, idx) => {
        const sessionNumber = idx + 1;
        return {
          session: {
            id: sessionNumber,
            lectureId: lecture?.item?.id ?? 0,
            sessionNumber: sessionNumber,
            sessionName: null,
            attendanceRequired: lecture?.item?.attendanceRequired ?? true,
            ...baseEntity,
          },
          attendance: [],
        };
      });
    }, [attendanceSessions, lecture]);

    const plannedStartTime = useMemo(
      () =>
        lecture?.schedule?.startTime
          ? new Date(lecture.schedule.startTime)
          : null,
      [lecture?.schedule?.startTime],
    );
    const plannedEndTime = useMemo(
      () =>
        lecture?.schedule?.endTime ? new Date(lecture.schedule.endTime) : null,
      [lecture?.schedule?.endTime],
    );

    const plannedBreakMinutes = useMemo(() => {
      const planned = lecture?.item?.lectureBreakMinutes;
      return planned !== null && planned !== undefined ? planned : 0;
    }, [lecture?.item?.lectureBreakMinutes]);

    const handleCompleteLecture = async (
      sessionDelta: AttendanceSessionPayload[],
    ) => {
      if (!plannedStartTime || !plannedEndTime) {
        toast.warn('לא נמצא זמן מתוכנן להרצאה');
        return;
      }

      if (
        startTime.getTime() < plannedStartTime.getTime() ||
        startTime.getTime() > plannedEndTime.getTime() ||
        endTime.getTime() < plannedStartTime.getTime() ||
        endTime.getTime() > plannedEndTime.getTime()
      ) {
        toast.warn(
          'זמני הביצוע חייבים להיות בתוך הזמן המתוכנן של ההרצאה ולא לחרוג ממנו',
        );
        return;
      }

      if (startTime.getTime() >= endTime.getTime()) {
        toast.warn('זמן ההתחלה חייב להיות לפני זמן הסיום');
        return;
      }

      if (addBreak) {
        if (
          breakMinutes === null ||
          breakMinutes === undefined ||
          !Number.isInteger(breakMinutes) ||
          breakMinutes < plannedBreakMinutes
        ) {
          toast.warn(
            plannedBreakMinutes > 0
              ? `יש להזין לפחות ${plannedBreakMinutes} דקות הפסקה (לפי התכנון)`
              : 'יש להזין מספר דקות הפסקה תקין (מספר שלם 0 או יותר)',
          );
          return;
        }

        const lectureDurationMinutes = Math.floor(
          (endTime.getTime() - startTime.getTime()) / (60 * 1000),
        );
        if (breakMinutes > lectureDurationMinutes) {
          toast.warn(
            'דקות ההפסקה לא יכולות להיות ארוכות ממשך זמן הביצוע שנבחר',
          );
          return;
        }
      }

      const completePayload = sessionDelta.map((entry) => ({
        sessionAttendanceId: entry.sessionId,
        attendance: entry.attendance,
      }));

      const programId = globalProgramStore.getCurrentId();
      await completeLectureOperation(
        programId,
        lecture,
        completePayload,
        startTime,
        endTime,
        addBreak ? breakMinutes : null,
      );
    };

    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!shouldAllowComplete && (
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div>* הרצאה זו אינה בוצעה ולכן לא ניתן לסמן נוכחות</div>
            <Button
              severity={'secondary'}
              outlined={true}
              style={{ height: '30px', fontSize: '14px', marginRight: '8px' }}
              label={'לחץ לביצוע ההרצאה'}
              onClick={() => setShouldAllowComplete(true)}
            />
          </div>
        )}
        {shouldAllowComplete && (
          <>
            {plannedStartTime && plannedEndTime && (
              <LectureTimePicker
                startTime={startTime}
                endTime={endTime}
                plannedStartTime={plannedStartTime}
                plannedEndTime={plannedEndTime}
                onStartTimeChange={setStartTime}
                onEndTimeChange={setEndTime}
              />
            )}

            <div
              style={{
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                alignItems: 'center',
                flexWrap: 'nowrap',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Checkbox
                  inputId="complete-lecture-add-break-checkbox"
                  checked={addBreak}
                  onChange={(e) => {
                    const next = Boolean(e.checked);
                    setAddBreak(next);
                    if (!next) {
                      setBreakMinutes(null);
                    } else if (
                      breakMinutes === null ||
                      breakMinutes === undefined ||
                      breakMinutes < plannedBreakMinutes
                    ) {
                      setBreakMinutes(plannedBreakMinutes);
                    }
                  }}
                />
                <label
                  htmlFor="complete-lecture-add-break-checkbox"
                  style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  הוספת הפסקה (בדקות)
                </label>
              </div>

              <InputNumber
                value={breakMinutes}
                onChange={(e) => {
                  if (e.value === undefined || e.value === null) {
                    setBreakMinutes(null);
                    return;
                  }
                  setBreakMinutes(
                    e.value < plannedBreakMinutes
                      ? plannedBreakMinutes
                      : e.value,
                  );
                }}
                min={plannedBreakMinutes}
                showButtons
                useGrouping={false}
                disabled={!addBreak}
                inputStyle={{ textAlign: 'right', width: '60px' }}
                style={{
                  height: '36px',
                  width: '120px',
                  direction: 'ltr',
                  flexShrink: 0,
                }}
              />
              {plannedBreakMinutes > 0 && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                  }}
                >
                  מינימום לפי תכנון: {plannedBreakMinutes} דק׳
                </span>
              )}
            </div>
            <AttendanceDataForm
              sessions={sessions}
              students={currentStudents}
              handleCompleteLecture={handleCompleteLecture}
              submitMode="full"
            />
          </>
        )}
      </div>
    );
  });
